import { useEffect, useRef } from 'react';
import { useAuthContext } from '@context/AuthContextProvider';
import { useNetworkStatus } from '@context/NetworkStatusContext';
import { envVariables } from '@fabric-ui/utils';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getAccountTokenFromLS } from '@utils/auth.utils';

const MAX_RETRY_COUNT = 3;

export type SSEEvent<T> = {
  event?: string;
  data: T;
  id?: string;
  retry?: number;
};

export type UseSSEOptions<T> = {
  url: string;
  enabled?: boolean;
  onMessage: (event: SSEEvent<T>) => void;
};

export const useSSE = <T,>({ url, enabled = true, onMessage }: UseSSEOptions<T>) => {
  const { accountId, refreshAccessToken } = useAuthContext();
  const { isOnline } = useNetworkStatus();
  const stateRef = useRef<{
    controller: AbortController | null;
    lastEventId: string | undefined;
    retryCount: number;
  }>({
    controller: null,
    lastEventId: undefined,
    retryCount: 0,
  });

  useEffect(() => {
    if (!enabled || !isOnline) {
      return undefined;
    }
    stateRef.current.retryCount = 0;

    const refreshAndReconnect = async () => {
      if (stateRef.current.retryCount >= MAX_RETRY_COUNT) {
        return;
      }
      stateRef.current.retryCount++;
      try {
        await refreshAccessToken(accountId);
      } catch {
        return;
      }
      startConnection();
    };

    const startConnection = () => {
      stateRef.current.controller?.abort();
      stateRef.current.controller = new AbortController();

      const fullUrl = `${envVariables.VITE_API_URL}/${url}`;

      fetchEventSource(fullUrl, {
        headers: {
          Authorization: `Bearer ${getAccountTokenFromLS(accountId)}`,
          ...(stateRef.current.lastEventId && { 'Last-Event-ID': stateRef.current.lastEventId }),
        },
        signal: stateRef.current.controller.signal,
        openWhenHidden: true,

        async onopen(response) {
          if (response.ok) {
            stateRef.current.retryCount = 0;
          } else {
            throw new Error(`HTTP ${response.status}`);
          }
        },

        onmessage(event) {
          if (!event.id) return;
          stateRef.current.lastEventId = event.id;
          const parsedData = event.data ? JSON.parse(event.data) : null;
          const sseEvent: SSEEvent<T> = {
            ...event,
            data: parsedData,
            id: event.id,
          };
          onMessage(sseEvent);
        },

        onclose() {
          throw new Error('Server closed connection');
        },

        onerror(err) {
          // Always throw to stop built-in retry (which reuses stale token in headers)
          throw err;
        },
      }).catch(() => {
        if (stateRef.current.controller?.signal.aborted) {
          return;
        }
        refreshAndReconnect();
      });
    };

    startConnection();
    return () => {
      stateRef.current.controller?.abort();
    };
  }, [url, enabled, onMessage, isOnline]);
};
